/** projection.default — RULES-ENGINE.md. Parameters come from funnels/<code>.json → rules. */
import type { AnswerMap } from '../answers.ts';
import type { Band, ProjectionRule } from '../types.ts';
import { formatNum } from '../slider.ts';

export interface ProjectionTile {
  key: string;
  from: number;
  to: number;
  fromLabel: string;
  toLabel: string;
}

export interface Projection {
  factor: number;
  multipliers: Record<string, number>;
  tiles: ProjectionTile[];
  chart: number[];
  fallback: boolean;
  inputs: Record<string, number | string>;
}

export const PROJECTION_DEFAULTS = { followers: 480, best_views: 1200, cadence: '1-2' } as const;

/** multiplier of the band containing v; the last band is open-ended. */
export function band(v: number, bands: Band[]): number {
  for (let i = 0; i < bands.length; i++) {
    const [from, to, mult] = bands[i];
    if (v >= from && (v < to || i === bands.length - 1)) return mult;
  }
  return bands[bands.length - 1][2];
}

/** round to `digits` significant figures (500 → 1500, 5000 → 5900). */
export function roundSig(v: number, digits = 2): number {
  if (!Number.isFinite(v) || v === 0) return 0;
  const mag = Math.floor(Math.log10(Math.abs(v))) - (digits - 1);
  const step = Math.pow(10, mag);
  return Math.round(v / step) * step;
}

const nearest = (v: number, options: number[]): number =>
  options.reduce((best, o) => (Math.abs(o - v) < Math.abs(best - v) ? o : best), options[0]);

function readNumber(answers: AnswerMap, key: string): number | undefined {
  const a = answers[key] as { value?: unknown } | undefined;
  return typeof a?.value === 'number' ? a.value : undefined;
}

function readString(answers: AnswerMap, key: string): string | undefined {
  const a = answers[key] as { value?: unknown } | undefined;
  return typeof a?.value === 'string' ? a.value : undefined;
}

export function computeProjection(rule: ProjectionRule, answers: AnswerMap): Projection {
  let fallback = false;

  let followers = readNumber(answers, 'followers');
  let bestViews = readNumber(answers, 'best_views');
  let cadence = readString(answers, 'cadence');
  if (followers === undefined || bestViews === undefined || cadence === undefined) {
    fallback = true;
    followers = followers ?? PROJECTION_DEFAULTS.followers;
    bestViews = bestViews ?? PROJECTION_DEFAULTS.best_views;
    cadence = cadence ?? PROJECTION_DEFAULTS.cadence;
  }
  // guard: a best reel under 100 views projects from 100
  const bestFrom = Math.max(100, bestViews);

  const boost = rule.cadenceBoost?.[cadence] ?? 1;
  const multipliers: Record<string, number> = {
    best_views: band(bestViews, rule.multipliers.best_views.bands) * boost,
    followers: band(followers, rule.multipliers.followers.bands) * boost,
  };

  const source = rule.headlineFactor?.source ?? 'best_views';
  const factor = nearest(multipliers[source] ?? 1, rule.headlineFactor?.round ?? [2, 2.5, 3]);

  const froms: Record<string, number> = { best_views: bestFrom, followers };
  const tiles: ProjectionTile[] = (rule.tiles ?? []).map((t) => {
    const key = t.from.replace(/^answers\./, '');
    const from = froms[key] ?? readNumber(answers, key) ?? 0;
    const to = roundSig(from * (multipliers[key] ?? 1), 2);
    return { key, from, to, fromLabel: formatNum(from), toLabel: formatNum(to) };
  });

  const head = tiles[0] ?? { from: bestFrom, to: roundSig(bestFrom * multipliers.best_views, 2) };
  const chart = (rule.chart?.points ?? [0.08, 0.32, 0.72, 1]).map((p) => Math.round(p * (head.to - head.from) + head.from));

  return { factor, multipliers, tiles, chart, fallback, inputs: { followers, best_views: bestViews, cadence } };
}
